import { useCallback, useEffect, useState } from 'react';

import {
  type IBaseModalProps,
  type IFolderDTO,
  blockMultipleSpaces,
} from '@gbs-monorepo-packages/common';

import { type IUpdateFolderProps } from '../../../../services/folders';
import {
  ContainerWithError,
  ErrorMessage,
  Fieldset,
  FormModalCustom,
  Grid,
  Input,
  Label,
} from './styles';

interface IEditFolderModalProps
  extends Partial<Omit<IBaseModalProps, 'children' | 'onOpenChange'>> {
  folder: IFolderDTO;
  onAccept: (folder: IUpdateFolderProps) => Promise<boolean>;
  onDecline: () => void;
  open: boolean;
  loading: boolean;
}

export const EditFolderModal = ({
  folder,
  onAccept,
  onDecline,
  open,
  loading,
  zIndex,
  ...props
}: IEditFolderModalProps): JSX.Element | null => {
  interface IError {
    name?: string;
  }

  const [error, setError] = useState<IError>({});
  const [name, setName] = useState('');

  const resetForm = useCallback(() => {
    setName(folder.name ?? '');
  }, []);

  useEffect(() => {
    resetForm();
  }, []);

  const handleDecline = useCallback(() => {
    onDecline?.();
  }, [onDecline]);

  const handleEditFolder = useCallback(async () => {
    let errors = 0;
    const errorsMessage = {
      name: '',
    };

    if (!name.trim()) {
      errorsMessage.name = 'Name is required.';
      errors++;
    }

    if (errors > 0) {
      setError(errorsMessage);
      return;
    }

    setError({});
    const aux: IUpdateFolderProps = {
      id: folder.id,
      name: name.trim(),
      typeFolder: folder.typeFolder,
    };

    await onAccept(aux).then((result: boolean) => {
      if (result) {
        handleDecline();
      }
    });
  }, [name, folder, onAccept, handleDecline]);

  return !open ? null : (
    <FormModalCustom
      loading={loading}
      biggerModal
      dataCy="modal-editFolder"
      {...props}
      open={open}
      acceptText="Update"
      declineText="Cancel"
      mainText="Edit Folder"
      onAccept={() => {
        void handleEditFolder();
      }}
      onDecline={handleDecline}
      onOpenChange={handleDecline}
      zIndex={zIndex}
    >
      <Grid>
        <ContainerWithError>
          <Fieldset filled={!!name} isInvalid={!!error.name}>
            <Input
              data-cy="input-name"
              value={name}
              autoComplete="off"
              id="name-input"
              name="name"
              maxLength={50}
              onChange={(e) => {
                if (e.target.value.length <= 50) {
                  setName(e.target.value);
                }
              }}
              onBlur={(e) => {
                setName(blockMultipleSpaces(e.target.value));
              }}
              required
              type="text"
            />
            <Label data-cy="label-name" htmlFor="name-input">
              Name
            </Label>
          </Fieldset>
          <ErrorMessage data-cy={error.name}>{error.name}</ErrorMessage>
        </ContainerWithError>
      </Grid>
    </FormModalCustom>
  );
};
